import { canv } from "@/canv/global.js";
import { DEFAULT_TEXT_COLOR, LINK_COLOR, STRIKE_COLOR } from "../constantsV2";
import type { TextLine, TextV2Data } from "../typesV2";
import {
    calculateLineWidth,
    calculateStrikeMetrics,
    calculateUnderlineMetrics,
    getBaseFont,
    getLineStartX,
    getListBulletRadius,
    getListBulletX,
    getListNumberX,
    getSegmentFont,
    getSegmentWidth,
    getTextStartX,
    setCanvasFont,
} from "./measurements";

export const drawLinePlaceholder = (
    line: TextLine,
    lineY: number,
    data: TextV2Data,
    startX: number,
    horizontalPadding: number,
    verticalPadding: number,
    lineHeight: number,
    precomputedLineWidth?: number,
): void => {
    if (line.segments.length === 0) {
        return;
    }
    const lineWidth =
        precomputedLineWidth ?? calculateLineWidth(line, data, horizontalPadding, lineHeight);
    const lineStartX = getLineStartX(lineWidth, startX, data.textAlign);
    const rectY = lineY - lineHeight + verticalPadding;
    const rectHeight = lineHeight - 2 * verticalPadding;

    canv.ctx.save();
    canv.ctx.fillStyle = "#9c9c9c";
    canv.ctx.fillRect(lineStartX, rectY, lineWidth, rectHeight);
    canv.ctx.restore();
};

export const drawListBullet = (
    lineStartX: number,
    lineY: number,
    color: string,
    horizontalPadding: number,
    lineHeight: number,
): void => {
    const bulletedRadius = getListBulletRadius(lineHeight);
    const cx = getListBulletX(lineStartX, horizontalPadding, lineHeight);
    const cy = lineY - lineHeight / 2;

    canv.ctx.save();
    canv.ctx.fillStyle = color;
    canv.ctx.beginPath();
    canv.ctx.arc(cx, cy, bulletedRadius, 0, Math.PI * 2, false);
    canv.ctx.closePath();
    canv.ctx.fill();
    canv.ctx.restore();
};

export const drawListNumber = (
    line: TextLine,
    lineStartX: number,
    y: number,
    color: string,
    data: TextV2Data,
    horizontalPadding: number,
): void => {
    const numberText = `${line.listNumber ?? 1}.`;
    const numberX = getListNumberX(lineStartX, horizontalPadding);

    canv.ctx.save();
    setCanvasFont(getBaseFont(data));
    canv.ctx.fillStyle = color;
    canv.ctx.fillText(numberText, numberX, y);
    canv.ctx.restore();
};

export const drawStrikeLine = (startX: number, y: number, width: number, height: number): void => {
    canv.ctx.save();
    canv.ctx.fillStyle = STRIKE_COLOR;
    canv.ctx.fillRect(startX, y, width, height);
    canv.ctx.restore();
};

export const drawUnderlineLine = (
    startX: number,
    y: number,
    width: number,
    height: number,
    color: string,
) => {
    canv.ctx.save();
    canv.ctx.fillStyle = color;
    canv.ctx.fillRect(startX, y, width, height);
    canv.ctx.restore();
};

export const drawUnderline = (
    line: TextLine,
    lineStartX: number,
    lineY: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
) => {
    const { underlineHeight, underlineY } = calculateUnderlineMetrics(lineHeight, lineY);

    let underlineStartX: number | null = null;
    let underlineWidth = 0;
    let underlineColor: string | null = null;

    let currentX = getTextStartX(line, lineStartX, data, horizontalPadding, lineHeight);

    for (const segment of line.segments) {
        const segmentWidth = getSegmentWidth(segment, data);

        const shouldUnderline = !!segment.underline || !!segment.link;
        const currentUnderlineColor = segment.link ? LINK_COLOR : STRIKE_COLOR;

        if (shouldUnderline) {
            if (underlineStartX == null) {
                underlineStartX = currentX;
                underlineWidth = segmentWidth;
                underlineColor = currentUnderlineColor;
            } else if (underlineColor === currentUnderlineColor) {
                underlineWidth += segmentWidth;
            } else {
                if (!underlineColor) {
                    return;
                }

                drawUnderlineLine(
                    underlineStartX,
                    underlineY,
                    underlineWidth,
                    underlineHeight,
                    underlineColor,
                );

                underlineStartX = currentX;
                underlineWidth = segmentWidth;
                underlineColor = currentUnderlineColor;
            }
        } else {
            if (underlineStartX !== null && underlineWidth > 0 && underlineColor) {
                drawUnderlineLine(
                    underlineStartX,
                    underlineY,
                    underlineWidth,
                    underlineHeight,
                    underlineColor,
                );
            }

            underlineStartX = null;
            underlineWidth = 0;
            underlineColor = null;
        }

        currentX += segmentWidth;
    }

    if (underlineStartX !== null && underlineWidth > 0 && underlineColor) {
        drawUnderlineLine(
            underlineStartX,
            underlineY,
            underlineWidth,
            underlineHeight,
            underlineColor,
        );
    }
};

/**
 * Рисует линии зачеркивания для зачеркнутых сегментов текста.
 * Объединяет последовательные зачеркнутые сегменты в одну линию.
 */
export const drawStrike = (
    line: TextLine,
    lineStartX: number,
    lineY: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): void => {
    const { strikeHeight, strikeY } = calculateStrikeMetrics(lineHeight, lineY);

    let strikeStartX: number | null = null;
    let strikeWidth = 0;
    let currentX = getTextStartX(line, lineStartX, data, horizontalPadding, lineHeight);

    for (const segment of line.segments) {
        const segmentWidth = getSegmentWidth(segment, data);

        if (segment.strike) {
            if (strikeStartX === null) {
                strikeStartX = currentX;
            }
            strikeWidth += segmentWidth;
        } else {
            if (strikeStartX !== null && strikeWidth > 0) {
                drawStrikeLine(strikeStartX, strikeY, strikeWidth, strikeHeight);
            }
            strikeStartX = null;
            strikeWidth = 0;
        }

        currentX += segmentWidth;
    }

    if (strikeStartX !== null && strikeWidth > 0) {
        drawStrikeLine(strikeStartX, strikeY, strikeWidth, strikeHeight);
    }
};

export const drawListMarker = (
    line: TextLine,
    lineStartX: number,
    lineY: number,
    textY: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): void => {
    const firstSegmentColor = line.segments[0]?.color || DEFAULT_TEXT_COLOR;

    if (line.listType === "bullet") {
        drawListBullet(lineStartX, lineY, firstSegmentColor, horizontalPadding, lineHeight);
    } else if (line.listType === "ordered") {
        drawListNumber(line, lineStartX, textY, firstSegmentColor, data, horizontalPadding);
    }
};

export const drawTextSegments = (
    line: TextLine,
    lineStartX: number,
    textY: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): void => {
    let currentX = getTextStartX(line, lineStartX, data, horizontalPadding, lineHeight);

    for (const segment of line.segments) {
        const font = getSegmentFont(segment, data);
        setCanvasFont(font);
        canv.ctx.fillStyle = segment.link ? LINK_COLOR : segment.color;
        canv.ctx.fillText(segment.text, currentX, textY);

        const segmentWidth = getSegmentWidth(segment, data);
        currentX += segmentWidth;
    }
};

export const drawLine = (
    line: TextLine,
    lineY: number,
    data: TextV2Data,
    startX: number,
    horizontalPadding: number,
    verticalPadding: number,
    lineHeight: number,
    precomputedLineWidth?: number,
): void => {
    const lineWidth =
        precomputedLineWidth ?? calculateLineWidth(line, data, horizontalPadding, lineHeight);
    const lineStartX = getLineStartX(lineWidth, startX, data.textAlign);
    const textY = lineY - verticalPadding;

    drawListMarker(line, lineStartX, lineY, textY, data, horizontalPadding, lineHeight);
    drawTextSegments(line, lineStartX, textY, data, horizontalPadding, lineHeight);
    drawStrike(line, lineStartX, lineY, data, horizontalPadding, lineHeight);
    drawUnderline(line, lineStartX, textY, data, horizontalPadding, lineHeight);
};
